import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { AlertTriangle, X, Trash2, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export function DemoDataBanner() {
  const { user } = useAuth();
  const [active, setActive] = useState(false);
  const [total, setTotal] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/spq/demo-data-status`).then(r => {
      if (r.data.active) { setActive(true); setTotal(r.data.total_records); }
    }).catch(() => {});
  }, []);

  const handleClear = async () => {
    setClearing(true);
    try {
      const { data } = await axios.post(`${API}/api/spq/clear-demo-data`);
      toast.success(`Cleared ${data.records_cleared} demo records. Your own data is preserved.`);
      setActive(false);
      setShowConfirm(false);
    } catch { toast.error('Failed to clear demo data'); }
    finally { setClearing(false); }
  };

  // v3.18 — team_members shouldn't see the Sample Data / Clear Demo controls.
  if (user?.role === 'team_member') return null;
  if (!active || dismissed) return null;

  return (
    <>
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="demo-data-banner">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-amber-800 text-xs sm:text-sm">
            <strong>Sample Data Active</strong> — This dashboard shows demo data to guide you. Edit or delete anytime.
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => setShowConfirm(true)} data-testid="clear-demo-btn">
            <Trash2 className="h-3 w-3 mr-1" />Clear Demo Data
          </Button>
          <button onClick={() => setDismissed(true)} className="text-amber-400 hover:text-amber-600"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md" data-testid="clear-demo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Before You Clear Demo Data
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-800 mb-2">Have you finished editing with your own company details?</p>
              <p className="text-xs text-amber-700">
                Demo data was pre-loaded to help you get started. Make sure you've replaced sample information with your real business data in:
              </p>
              <ul className="mt-2 space-y-1 text-xs text-amber-700">
                <li className="flex items-center gap-1.5">Products & Services</li>
                <li className="flex items-center gap-1.5">Client List</li>
                <li className="flex items-center gap-1.5">Company Profile & Branding</li>
                <li className="flex items-center gap-1.5">Attendance & HR Settings</li>
              </ul>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-xs text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                <strong>Your own data is safe.</strong> Only {total} sample records will be removed. Anything you created or edited yourself will remain untouched.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowConfirm(false)} data-testid="cancel-clear-btn">
              Not Yet, Go Back
            </Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleClear} disabled={clearing} data-testid="confirm-clear-btn">
              {clearing ? 'Clearing...' : "Yes, I'm Ready — Clear Demo Data"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
